import { scaleLinear } from 'd3';
import { clamp, min } from 'lodash';
import { useMemo } from 'react';

import { PatternDiagonalStripe } from '../../svg-patterns/patterns';
import { getMaxTextWidth, getOtherColor, useChartTheme } from '../../utilities';
import { AxisLabel } from '../axis/axis-label';
import styles from './stacked-bar-chart.module.css';
import { buildGroupAxisLayout } from './stacked-bar-group-axis';
import {
  buildSegmentEntries,
  Dimensions,
  StackedBarLegend,
  type StackedBarRendererProps,
} from './stacked-bar-shared';
import {
  findNearestBarIndex,
  findSegmentAtValue,
  StackedBarSharedTooltip,
  type StackedBarTooltipState,
  useStackedBarTooltip,
} from './stacked-bar-tooltip';

const EMPTY_BAR_PATTERN_ID = 'diagonal-stripe-4';

export const HorizontalStackedBar = ({
  model,
  width,
  height,
  format,
  hideLegend,
  maxLabelWidth,
  formatLegendLabel,
  maxLegendLabelWidth,
  isTimeseries = false,
  highlightMatchingSegments = true,
}: StackedBarRendererProps) => {
  const {
    dataIndexed,
    displayLabels,
    segmentLabels,
    stackedSeries,
    emptyBars,
    colorScale,
    domainMax,
  } = model;

  const { isNewTheme } = useChartTheme();

  const paddingInner = isNewTheme
    ? Dimensions.NewBandPaddingInner
    : Dimensions.BandPaddingFactor;
  const paddingOuter = isNewTheme
    ? Dimensions.NewBandPaddingOuter
    : Dimensions.BandPaddingFactor;
  const segmentSpacing = isNewTheme
    ? Dimensions.NewSegmentSpacing
    : Dimensions.SegmentSpacing;
  const legendHeight = hideLegend ? 0 : Dimensions.LegendHeight;
  const minHeight = dataIndexed.length * Dimensions.MinBarThickness;
  const maxHeight = dataIndexed.length * Dimensions.MaxBarThickness;
  const plotHeight = clamp(height - legendHeight, minHeight, maxHeight);

  const groupAxis = useMemo(
    () =>
      buildGroupAxisLayout({
        labels: dataIndexed.map((d) => d.label),
        isTimeseries,
        rangeLength: plotHeight,
        dimensions: { paddingInner, paddingOuter },
      }),
    [dataIndexed, isTimeseries, plotHeight, paddingInner, paddingOuter]
  );
  const barHeight = groupAxis.barThickness;
  const focusHoveredOnly = !highlightMatchingSegments;

  const barStarts = useMemo(
    () => dataIndexed.map((_, index) => groupAxis.getBarStart(index)),
    [dataIndexed, groupAxis]
  );
  const emptyIndexSet = useMemo(
    () => new Set(emptyBars.map((bar) => bar._index)),
    [emptyBars]
  );

  const axisLabelTexts = groupAxis.isTime
    ? groupAxis.ticks.map((tick) => tick.label)
    : displayLabels;
  const labelWidth = min([
    getMaxTextWidth({
      texts: axisLabelTexts,
      fontSize: 11,
    }),
    maxLabelWidth,
  ]) as number;
  const plotWidth = Math.max(0, width - labelWidth - Dimensions.LabelPadding);
  const chartOffsetX = labelWidth + Dimensions.LabelPadding;

  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;
  const shouldHideLegend = hideLegend || legendHeight + plotHeight > height;

  const xScale = useMemo(
    () => scaleLinear().domain([0, domainMax]).range([0, plotWidth]),
    [domainMax, plotWidth]
  );

  const yAxisEntries = useMemo(
    () =>
      groupAxis.isTime
        ? groupAxis.ticks.map((tick, index) => ({
            key: `tick-${index}`,
            y: tick.coord,
            label: tick.label,
          }))
        : dataIndexed.map((d, index) => {
            const start = barStarts[index];
            return {
              key: `axis-${index}`,
              y: start === undefined ? undefined : start + barHeight / 2,
              label: displayLabels?.[index] ?? d.label,
            };
          }),
    [groupAxis, dataIndexed, displayLabels, barStarts, barHeight]
  );

  const hitTest = ([px, py]: [
    number,
    number,
  ]): StackedBarTooltipState | null => {
    const row = findNearestBarIndex(barStarts, barHeight, py);
    if (row === null) return null;
    const start = barStarts[row];
    const svgY = start === undefined ? py : start + barHeight / 2;

    if (emptyIndexSet.has(row)) {
      return {
        key: `empty:${row}`,
        svgX: chartOffsetX + plotWidth / 2,
        svgY,
        title: dataIndexed[row]?.label ?? '',
        entries: undefined,
      };
    }

    const hit = findSegmentAtValue(stackedSeries, row, xScale, px);
    if (hit === null) return null;
    return {
      key: `${row}:${hit.series.key}`,
      svgX:
        chartOffsetX + (xScale(hit.segment[0]) + xScale(hit.segment[1])) / 2,
      svgY,
      title: hit.series.key,
      entries: buildSegmentEntries(
        hit.series,
        hit.segment,
        format,
        focusHoveredOnly
      ),
    };
  };

  const { state, isVisible, gProps } = useStackedBarTooltip(hitTest);

  const barsContent = useMemo(
    () => (
      <>
        {stackedSeries.map((series) => (
          <g
            key={`series-${series.key}`}
            fill={colorScale(series.key) ?? getOtherColor()}
            className={styles.bar}
          >
            {series.map((segment) => {
              const y = barStarts[segment.data._index];
              if (y === undefined) return null;
              const x = xScale(segment[0]);
              const segmentWidth = Math.max(
                0,
                xScale(segment[1]) - xScale(segment[0]) - segmentSpacing
              );
              if (segmentWidth === 0) return null;
              return (
                <rect
                  key={`segment-${series.key}-${segment.data._index}`}
                  x={x}
                  y={y}
                  fill={colorScale(series.key)}
                  width={segmentWidth}
                  height={barHeight}
                  className={styles.segment}
                  rx={isNewTheme ? 0 : 2}
                  cursor="pointer"
                />
              );
            })}
          </g>
        ))}
        {emptyBars.map((emptyBar) => {
          const y = barStarts[emptyBar._index];
          if (y === undefined) return null;
          return (
            <rect
              key={`empty-bar-${emptyBar.label}-${emptyBar._index}`}
              x={0}
              y={y}
              width={plotWidth}
              height={barHeight}
              className={styles.emptyBar}
              cursor="pointer"
              fill={`url(#${EMPTY_BAR_PATTERN_ID}) #cbd5e0`}
            />
          );
        })}
      </>
    ),
    [
      stackedSeries,
      emptyBars,
      barStarts,
      barHeight,
      xScale,
      segmentSpacing,
      colorScale,
      isNewTheme,
      plotWidth,
    ]
  );

  return (
    <div style={{ width: '100%' }}>
      <div className={styles.plotViewport}>
        <svg width="100%" height={plotHeight}>
          <defs>
            <PatternDiagonalStripe patternId={EMPTY_BAR_PATTERN_ID} />
          </defs>
          <g id="y-axis">
            {isPlotSizeValid &&
              yAxisEntries.map((entry) =>
                entry.y === undefined ? null : (
                  <AxisLabel
                    testId="stacked-horizontal-bar-chart-y-axis-label"
                    key={entry.key}
                    y={entry.y}
                    width={labelWidth}
                    availableHeight={groupAxis.isTime ? 16 : barHeight + 2}
                    label={entry.label}
                  />
                )
              )}
          </g>
          <g id="chart" transform={`translate(${chartOffsetX}, 0)`}>
            {isPlotSizeValid && (
              <g
                className={
                  focusHoveredOnly
                    ? styles.barContainerFocus
                    : styles.barContainer
                }
                {...gProps}
              >
                {barsContent}
              </g>
            )}
          </g>
        </svg>
        <StackedBarSharedTooltip state={state} isVisible={isVisible} />
      </div>
      {!shouldHideLegend && isPlotSizeValid && (
        <StackedBarLegend
          className={styles.legendContainer}
          segmentLabels={segmentLabels}
          colorScale={colorScale}
          formatLegendLabel={formatLegendLabel}
          maxLegendLabelWidth={maxLegendLabelWidth}
          marginLeft={labelWidth + Dimensions.LabelPadding}
        />
      )}
    </div>
  );
};
